import React from "react";
import { Button, Flex, Image, Text } from "@chakra-ui/react";
import emptyConditionsSvg from "assets/empty-conditions.svg";

interface ConditionsEmptyStateProps {
  /** Called when the user clicks the "Criar condição" button */
  onCreateCondition: () => void;
  /** Translation function */
  t: (key: string) => string;
  /** Whether to show the "select a risk above" suggestion text. Defaults to true.
   *  Should be false in Permissive mode since risk categories are hidden. */
  showRiskSuggestion?: boolean;
}

/**
 * ConditionsEmptyState - Empty state shown inside the conditions section
 * when the user opens the Event creation drawer with "Sem categoria" selected
 * and hasn't created any conditions yet.
 *
 * Design: Figma node 2733:34561
 */
export const ConditionsEmptyState: React.FC<ConditionsEmptyStateProps> = ({
  onCreateCondition,
  t,
  showRiskSuggestion = true,
}) => {
  return (
    <Flex
      bg="rgba(232, 236, 240, 0.4)"
      border="1px solid #EFEFF0"
      borderRadius="8px"
      padding="16px"
      direction="column"
      align="center"
      justify="center"
      w="100%"
    >
      <Flex direction="column" gap="10px" align="center">
        {/* Illustration */}
        <Image
          src={emptyConditionsSvg}
          alt=""
          w="146px"
          h="128px"
          pointerEvents="none"
          userSelect="none"
        />

        {/* Text + Button block */}
        <Flex direction="column" gap="12px" align="center" w="314px">
          {/* Title */}
          <Flex direction="column" align="center" w="100%">
            <Text
              fontWeight="500"
              fontSize="14px"
              lineHeight="20px"
              color="#0884CC"
              textAlign="center"
              w="253px"
            >
              {t("conditionsEmptyStateTitle")}
            </Text>
          </Flex>

          {/* Button + Subtitle */}
          <Flex direction="column" gap="4px" align="center" w="100%">
            {/* Create condition button */}
            <Button
              bg="#0884CC"
              color="#FCFCFC"
              fontSize="14px"
              fontWeight="500"
              height="36px"
              minW="116px"
              w="100%"
              borderRadius="6px"
              _hover={{ bg: "#0773B0" }}
              _active={{ bg: "#066399" }}
              onClick={onCreateCondition}
            >
              {t("conditionsEmptyStateCreateButton")}
            </Button>

            {/* Subtitle - hidden in Permissive mode since risk categories are not shown */}
            {showRiskSuggestion && (
              <Text
                fontWeight="400"
                fontSize="12px"
                lineHeight="20px"
                color="#4A6075"
                textAlign="center"
                w="314px"
              >
                {t("conditionsEmptyStateSubtext")}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
